/**
 * consent.ts — Ouderlijke toestemming geven en intrekken.
 *
 * Dit is de schakelaar die de toestemmingsmuur uit migratie 0010 opent en
 * sluit. Alles wat een OORDEEL over een kind is — toetsuitslagen, rapporten,
 * niveaubepalingen — mag pas bewaard worden als hier toestemming staat.
 * Oefenvoortgang valt daarbuiten en werkt altijd.
 *
 * Bewust een aparte handeling van het koppelen. Koppelen is "ik wil
 * meekijken"; toestemming is een juridische verklaring die aantoonbaar en
 * intrekbaar moet zijn (AVG art. 7). Zou een ouder toestemming geven door
 * simpelweg een code in te typen, dan is dat geen geïnformeerde toestemming.
 *
 * @see supabase/migrations/0005 — grant_parental_consent
 * @see supabase/migrations/0010 — withdraw_parental_consent + de muur
 */

import { supabase } from './client'
import { error as logError } from '../../utils/logger'

export interface ConsentResult {
  ok: boolean
  message?: string
  /** Bij intrekken: wat er is gewist. Verantwoording, AVG art. 5 lid 2. */
  deleted?: { reports: number; attempts: number }
}

export async function grantConsent(learnerId: string): Promise<ConsentResult> {
  const { error } = await supabase.rpc('grant_parental_consent', {
    p_learner_id: learnerId,
  })
  if (error) {
    logError('[Consent] Toestemming geven mislukt', error)
    // De database controleert zelf of de aanroeper een gekoppelde ouder is.
    // Faalt dat, dan is dat geen technische storing maar een geweigerde actie.
    return {
      ok: false,
      message:
        'Toestemming geven is niet gelukt. Controleer of dit kind aan je account gekoppeld is.',
    }
  }
  return { ok: true }
}

export async function withdrawConsent(
  learnerId: string,
): Promise<ConsentResult> {
  const { data, error } = await supabase.rpc('withdraw_parental_consent', {
    target_learner: learnerId,
  })
  if (error) {
    logError('[Consent] Toestemming intrekken mislukt', error)
    return { ok: false, message: 'Intrekken is niet gelukt. Probeer opnieuw.' }
  }
  const res = (data ?? {}) as Record<string, number>
  return {
    ok: true,
    deleted: {
      reports: res.reports_deleted ?? 0,
      attempts: res.attempts_deleted ?? 0,
    },
  }
}
